import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

// ---------------------------------------------------------------------------
// Hubs = Finland-wide topics (national parks, saunas, ski resorts) that live
// alongside the cities. A hub NEVER owns a place: it references the single
// `places` record through `place_hubs`, so one place can sit in several hubs
// and several cities without being duplicated.
// ---------------------------------------------------------------------------

export type HubAttributes = Record<string, string | number | boolean | null>;

export type HubDTO = {
  id: string;
  slug: string;
  kind: string;
  name: string;
  name_fi: string | null;
  tagline: string | null;
  tagline_fi: string | null;
  description: string | null;
  description_fi: string | null;
  hero_image_url: string | null;
  seo_title: string | null;
  seo_description: string | null;
};

export type HubPlaceDTO = {
  id: string;
  slug: string;
  name: string;
  category: string | null;
  area: string | null;
  city_slug: string | null;
  cities: string[];
  description: string | null;
  image_url: string | null;
  gallery_urls: string[];
  lat: number | null;
  lng: number | null;
  tags: string[];
  link: string | null;
  /** Hub-specific fields, e.g. { municipality, region, established, area_km2, public_transport } */
  attributes: HubAttributes;
  featured: boolean;
  /** Food-hub extras (restaurants, cafés, bars, pubs). */
  price: string | null;
  subcategory: string | null;
  address: string | null;
  phone: string | null;
  opening_hours: OpeningHoursJson | null;
  menu_url: string | null;
  special_hours?: { date: string; closed?: boolean; open?: string | null; close?: string | null }[] | null;
  temporarily_closed?: boolean;
  schedule_info?: string | null;
};

export type HubProductDTO = {
  id: string;
  title: string;
  title_fi: string | null;
  description: string | null;
  description_fi: string | null;
  image_url: string | null;
  bokun_product_id: string | null;
  bokun_widget_url: string | null;
  external_url: string | null;
  price_from: string | null;
  duration: string | null;
};

/** Serializable shape of the stored opening-hours JSON. */
export type OpeningHoursJson = Record<
  string,
  string | { open?: string; close?: string; closed?: boolean } | null
>;

const PLACE_COLUMNS =
  "id, slug, name, category, subcategory, area, city_slug, description, description_fi, image_url, gallery_urls, lat, lng, tags, link, price, published, menu_external_url, menu_pdf_url, menu_image_url, schedule_info, address, phone, opening_hours, special_hours, temporarily_closed";

function mapPlace(row: any, link: any, cities: string[]): HubPlaceDTO {
  return {
    id: row.id,
    slug: row.slug,
    name: row.name,
    category: row.category ?? null,
    area: row.area ?? null,
    city_slug: row.city_slug ?? null,
    cities,
    description: row.description ?? null,
    image_url: row.image_url ?? null,
    gallery_urls: (row.gallery_urls ?? []) as string[],
    lat: row.lat ?? null,
    lng: row.lng ?? null,
    tags: (row.tags ?? []) as string[],
    link: row.link ?? null,
    attributes: (link?.attributes ?? {}) as HubAttributes,
    featured: Boolean(link?.featured),
    price: row.price ?? null,
    subcategory: row.subcategory ?? null,
    address: row.address ?? null,
    phone: row.phone ?? null,
    opening_hours: (row.opening_hours as OpeningHoursJson | null) ?? null,
    menu_url: row.menu_external_url || row.menu_pdf_url || row.menu_image_url || null,
    special_hours: (row.special_hours ?? null) as HubPlaceDTO["special_hours"],
    temporarily_closed: Boolean(row.temporarily_closed),
    schedule_info: row.schedule_info ?? null,
  };
}

/** Hub index page: the hub plus every published place referenced by it. */
export const getPublicHub = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => z.object({ slug: z.string().min(1).max(120) }).parse(d))
  .handler(async ({ data }): Promise<{ hub: HubDTO; places: HubPlaceDTO[] } | null> => {
    const { supabaseAdmin: db } = await import("@/integrations/supabase/client.server");
    const supabaseAdmin = db as any;

    const { data: hub } = await supabaseAdmin
      .from("hubs")
      .select("*")
      .eq("slug", data.slug)
      .eq("active", true)
      .maybeSingle();
    if (!hub) return null;

    const { data: links } = await supabaseAdmin
      .from("place_hubs")
      .select("place_id, attributes, featured, sort_order")
      .eq("hub_id", (hub as any).id)
      .order("sort_order", { ascending: true });

    const ids = (links ?? []).map((l: any) => l.place_id);
    if (ids.length === 0) return { hub: hub as unknown as HubDTO, places: [] };

    const [{ data: places }, { data: cityLinks }] = await Promise.all([
      supabaseAdmin.from("places").select(PLACE_COLUMNS).in("id", ids).eq("published", true),
      supabaseAdmin.from("place_cities").select("place_id, city_slug").in("place_id", ids),
    ]);

    const byId = new Map((places ?? []).map((p: any) => [p.id, p]));
    const cityMap = new Map<string, string[]>();
    for (const c of (cityLinks ?? []) as any[]) {
      cityMap.set(c.place_id, [...(cityMap.get(c.place_id) ?? []), c.city_slug]);
    }

    const result: HubPlaceDTO[] = [];
    for (const l of (links ?? []) as any[]) {
      const row = byId.get(l.place_id) as any;
      if (!row) continue;
      const cities = cityMap.get(l.place_id) ?? [];
      if (row.city_slug && !cities.includes(row.city_slug)) cities.unshift(row.city_slug);
      result.push(mapPlace(row, l, cities));
    }
    result.sort((a, b) => a.name.localeCompare(b.name));
    return { hub: hub as unknown as HubDTO, places: result };
  });

/** Hub child page: one place, its hub-specific fields and bookable products. */
export const getPublicHubPlace = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) =>
    z.object({ hubSlug: z.string().min(1).max(120), placeSlug: z.string().min(1).max(200) }).parse(d),
  )
  .handler(
    async ({
      data,
    }): Promise<{
      hub: HubDTO;
      place: HubPlaceDTO;
      products: HubProductDTO[];
      siblings: { slug: string; name: string; image_url: string | null }[];
    } | null> => {
      const { supabaseAdmin: db } = await import("@/integrations/supabase/client.server");
    const supabaseAdmin = db as any;

      const { data: hub } = await supabaseAdmin
        .from("hubs")
        .select("*")
        .eq("slug", data.hubSlug)
        .eq("active", true)
        .maybeSingle();
      if (!hub) return null;

      const { data: place } = await supabaseAdmin
        .from("places")
        .select(PLACE_COLUMNS + ", overview_body, details_body, address, phone, email, opening_hours, special_hours, temporarily_closed, schedule_info")
        .eq("slug", data.placeSlug)
        .eq("published", true)
        .maybeSingle();
      if (!place) return null;

      const { data: link } = await supabaseAdmin
        .from("place_hubs")
        .select("attributes, featured")
        .eq("hub_id", (hub as any).id)
        .eq("place_id", (place as any).id)
        .maybeSingle();
      if (!link) return null;

      const [{ data: cityLinks }, { data: products }, { data: siblingLinks }] = await Promise.all([
        supabaseAdmin.from("place_cities").select("city_slug").eq("place_id", (place as any).id),
        supabaseAdmin
          .from("place_products")
          .select("*")
          .eq("place_id", (place as any).id)
          .eq("active", true)
          .order("sort_order", { ascending: true }),
        supabaseAdmin.from("place_hubs").select("place_id").eq("hub_id", (hub as any).id).limit(200),
      ]);

      const placeRow = place as any;
      const cities = ((cityLinks ?? []) as any[]).map((c) => c.city_slug);
      if (placeRow.city_slug && !cities.includes(placeRow.city_slug)) {
        cities.unshift(placeRow.city_slug);
      }

      const siblingIds = ((siblingLinks ?? []) as any[])
        .map((l) => l.place_id)
        .filter((id) => id !== (place as any).id)
        .slice(0, 60);
      const { data: siblings } = siblingIds.length
        ? await supabaseAdmin
            .from("places")
            .select("slug, name, image_url")
            .in("id", siblingIds)
            .eq("published", true)
            .order("name")
            .limit(8)
        : { data: [] as any[] };

      return {
        hub: hub as unknown as HubDTO,
        place: { ...mapPlace(place, link, cities), ...({} as object) },
        products: (products ?? []) as HubProductDTO[],
        siblings: (siblings ?? []) as { slug: string; name: string; image_url: string | null }[],
      };
    },
  );

/** All active hubs — used by nav / the hub landing lists. */
export const listPublicHubs = createServerFn({ method: "GET" }).handler(async (): Promise<HubDTO[]> => {
  const { supabaseAdmin: db } = await import("@/integrations/supabase/client.server");
    const supabaseAdmin = db as any;
  const { data } = await supabaseAdmin
    .from("hubs")
    .select("*")
    .eq("active", true)
    .order("sort_order", { ascending: true });
  return (data ?? []) as unknown as HubDTO[];
});

/* ------------------------------------------------------------------ *
 * /menus — every place that actually has a menu (link, PDF, photo or
 * dish rows), browsable by cuisine, price range and region.
 * ------------------------------------------------------------------ */

export type MenuDirectoryEntry = {
  id: string;
  slug: string;
  name: string;
  image_url: string | null;
  city_slug: string | null;
  area: string | null;
  cuisine: string | null;
  price: string | null;
  listing_type: string | null;
  dish_count: number;
  menu_url: string | null;
};

export const getMenusDirectory = createServerFn({ method: "GET" }).handler(
  async (): Promise<MenuDirectoryEntry[]> => {
    const { supabaseAdmin: db } = await import("@/integrations/supabase/client.server");
    const supabaseAdmin = db as any;

    const { data: places } = await supabaseAdmin
      .from("places")
      .select(
        "id, slug, name, image_url, city_slug, area, category, subcategory, price, listing_type, menu_external_url, menu_pdf_url, menu_image_url",
      )
      .eq("published", true)
      .limit(1000);

    const { data: dishes } = await supabaseAdmin
      .from("place_menu_items")
      .select("place_id")
      .eq("is_active", true)
      .limit(5000);

    const counts = new Map<string, number>();
    for (const d of (dishes ?? []) as any[]) {
      counts.set(d.place_id, (counts.get(d.place_id) ?? 0) + 1);
    }

    const rows: MenuDirectoryEntry[] = [];
    for (const p of (places ?? []) as any[]) {
      const menuUrl = p.menu_external_url || p.menu_pdf_url || p.menu_image_url || null;
      const dishCount = counts.get(p.id) ?? 0;
      if (!menuUrl && dishCount === 0) continue;
      rows.push({
        id: p.id,
        slug: p.slug,
        name: p.name,
        image_url: p.image_url ?? null,
        city_slug: p.city_slug ?? null,
        area: p.area ?? null,
        cuisine: p.subcategory || p.category || null,
        price: p.price ?? null,
        listing_type: p.listing_type ?? null,
        dish_count: dishCount,
        menu_url: menuUrl,
      });
    }
    rows.sort((a, b) => b.dish_count - a.dish_count || a.name.localeCompare(b.name));
    return rows;
  },
);
